'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/lib/hooks/useAuth';
import PhoneVerificationPopup from '../../../components/PhoneVerificationPopup';
import ContractPopup from '../../../components/ContractPopup';
import { SmsCodeType } from '../../../lib/types/api';

interface RegisterFormProps {
  locale: string;
}

export default function RegisterForm({ locale }: RegisterFormProps) {
  const t = useTranslations('register');
  const footerT = useTranslations('footer');
  
  const { register, error, errors, clearError } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: '',
    email: '',
    phone: '',
    tcIdentityNumber: '',
    password: '',
    passwordConfirm: '',
    acceptTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [tcIdentityError, setTcIdentityError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [emailError, setEmailError] = useState("");
  const genderDropdownRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [verifiedSmsCode, setVerifiedSmsCode] = useState('');
  const [showContractPopup, setShowContractPopup] = useState(false);
  const [selectedContract, setSelectedContract] = useState('');
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasLowercase: false,
    hasUppercase: false,
    hasSpecialChar: false
  });
  
  // Sayfa yüklendiğinde error state'ini temizle
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (genderDropdownRef.current && !genderDropdownRef.current.contains(event.target as Node)) {
        // setGenderDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validatePhone = (phoneNumber: string) => {
    // 5xx xxx xx xx formatında olmalı
    const phoneRegex = /^5\d{2}\s\d{3}\s\d{2}\s\d{2}$/;
    return phoneRegex.test(phoneNumber);
  };

  const validateEmail = (email: string) => {
    // Email regex validasyonu
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateName = (name: string) => {
    // İsim en az 2 karakter, sadece harf ve boşluk
    const nameRegex = /^[a-zA-ZğüşıöçĞÜŞİÖÇ\s]{2,}$/;
    return nameRegex.test(name);
  };

  const validateTcIdentity = (tcNumber: string) => {
    // TC Kimlik Numarası: Tam olarak 11 karakter, sadece rakam
    const tcRegex = /^\d{11}$/;
    return tcRegex.test(tcNumber);
  };

  // Şifre gereksinimlerini kontrol et (state güncellemesi yapmadan)
  const checkPasswordRequirements = useCallback((password: string) => {
    return {
      minLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  }, []);

  const validatePasswordRequirements = useCallback((password: string) => {
    const requirements = checkPasswordRequirements(password);
    setPasswordRequirements(requirements);
    return Object.values(requirements).every(Boolean);
  }, [checkPasswordRequirements]);

  // Şifre validasyon durumunu memoize et (infinite loop'u önlemek için)
  const isPasswordValid = useMemo(() => {
    if (!formData.password) return false;
    const requirements = checkPasswordRequirements(formData.password);
    return Object.values(requirements).every(Boolean);
  }, [formData.password, checkPasswordRequirements]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Telefon validasyonu
    if (field === 'phone') {
      const phoneValue = value as string;
      
      // Sadece rakam karakterlerine izin ver
      let cleanValue = phoneValue.replace(/[^\d]/g, '');
      
      // Türkiye ülke kodu varsa kaldır (90)
      if (cleanValue.startsWith('90') && cleanValue.length > 10) {
        cleanValue = cleanValue.substring(2);
      }
      
      // Başındaki 0'ı kaldır
      if (cleanValue.startsWith('0')) {
        cleanValue = cleanValue.substring(1);
      }
      
      // Maksimum uzunluk kontrolü (5xx xxx xx xx = 10 rakam)
      if (cleanValue.length > 10) {
        cleanValue = cleanValue.substring(0, 10);
      }
      
      // Format: 5xx xxx xx xx
      let formattedValue = '';
      if (cleanValue.length > 0) {
        formattedValue = cleanValue.substring(0, 3);
      }
      if (cleanValue.length > 3) {
        formattedValue += ' ' + cleanValue.substring(3, 6);
      }
      if (cleanValue.length > 6) {
        formattedValue += ' ' + cleanValue.substring(6, 8);
      }
      if (cleanValue.length > 8) {
        formattedValue += ' ' + cleanValue.substring(8, 10);
      }
      
      // Formatted value'yu set et
      setFormData(prev => ({
        ...prev,
        [field]: formattedValue
      }));
      
      if (formattedValue && !validatePhone(formattedValue)) {
        setPhoneError(t('phoneError'));
      } else {
        setPhoneError("");
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Ad validasyonu
    if (field === 'firstName') {
      const firstNameValue = value as string;
      if (firstNameValue && !validateName(firstNameValue)) {
        setFirstNameError(t('firstNameError'));
      } else {
        setFirstNameError("");
      }
    }

    // Soyad validasyonu
    if (field === 'lastName') {
      const lastNameValue = value as string;
      if (lastNameValue && !validateName(lastNameValue)) {
        setLastNameError(t('lastNameError'));
      } else {
        setLastNameError("");
      }
    }

    // Email validasyonu
    if (field === 'email') {
      const emailValue = value as string;
      if (emailValue && !validateEmail(emailValue)) {
        setEmailError('Geçerli bir e-posta adresi girin');
      } else {
        setEmailError("");
      }
    }

    // TC Kimlik Numarası validasyonu
    if (field === 'tcIdentityNumber') {
      const tcValue = value as string;
      // Sadece rakam girişine izin ver ve maksimum 11 karakter
      const cleanValue = tcValue.replace(/[^\d]/g, '').substring(0, 11);
      setFormData(prev => ({
        ...prev,
        [field]: cleanValue
      }));
      
      if (cleanValue && !validateTcIdentity(cleanValue)) {
        setTcIdentityError('TC Kimlik Numarası tam olarak 11 rakam olmalıdır');
      } else {
        setTcIdentityError("");
      }
    }

    // Şifre validasyonu
    if (field === 'password') {
      const passwordValue = value as string;
      if (passwordValue) {
        // Requirements'ı güncelle
        const requirements = checkPasswordRequirements(passwordValue);
        setPasswordRequirements(requirements);
        
        const isValid = Object.values(requirements).every(Boolean);
        if (!isValid) {
          setPasswordError(t('passwordError'));
        } else {
          setPasswordError("");
        }
      } else {
        setPasswordError("");
        setPasswordRequirements({
          minLength: false,
          hasNumber: false,
          hasLowercase: false,
          hasUppercase: false,
          hasSpecialChar: false
        });
      }
    }
  };

  const handleVerifyPhone = () => {
    // Telefon numarası validasyonu
    if (!validatePhone(formData.phone)) {
      setPhoneError(t('phoneError'));
      return;
    }
    
    // Error state'ini temizle
    clearError();
    setShowPhoneVerification(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validasyonları kontrol et
    let hasError = false;
    
    if (!validatePhone(formData.phone)) {
      setPhoneError(t('phoneError'));
      hasError = true;
    }

    if (!validateName(formData.firstName)) {
      setFirstNameError(t('firstNameError'));
      hasError = true;
    }

    if (!validateName(formData.lastName)) {
      setLastNameError(t('lastNameError'));
      hasError = true;
    }

    // TC Kimlik Numarası validasyonu
    if (!validateTcIdentity(formData.tcIdentityNumber)) {
      setTcIdentityError('TC Kimlik Numarası tam olarak 11 rakam olmalıdır');
      hasError = true;
    }

    // Email validasyonu (opsiyonel alan)
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError('Geçerli bir e-posta adresi girin');
      hasError = true;
    }

    if (!validatePasswordRequirements(formData.password)) {
      setPasswordError(t('passwordError'));
      hasError = true;
    }

    if (formData.password !== formData.passwordConfirm) {
      alert(t('passwordMismatch'));
      return;
    }

    if (!formData.acceptTerms) {
      alert(t('acceptTerms'));
      return;
    }

    // Telefon doğrulaması kontrolü
    if (!isPhoneVerified) {
      setPhoneError(t('phoneNotVerified'));
      hasError = true;
    }

    if (hasError) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Remove spaces from phone number for API
      const phoneNumber = formData.phone.replace(/\s/g, '');
      
      const success = await register({
        phoneNumber,
        password: formData.password,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        tcIdentityNumber: formData.tcIdentityNumber,
        smsCode: verifiedSmsCode
      });
      
      if (success) {
        // Cookie'ler senkron olarak set edildi (js-cookie)
        // Browser history'yi tamamen temizle
        window.history.replaceState(null, '', window.location.href);
        
        // Redirect to home page after successful registration and auto-login
        // window.location.href kullanarak tam refresh yap
        window.location.href = `/${locale}`;
      }
    } catch (error) {
      // Error handling
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneVerificationSuccess = (smsCode: string) => {
    setIsPhoneVerified(true);
    setVerifiedSmsCode(smsCode);
    setShowPhoneVerification(false);
    setPhoneError(''); // Telefon hatasını temizle
    // Error state'ini temizle
    clearError();
  };

  const handleContractClick = (contractType: string) => {
    setSelectedContract(contractType);
    setShowContractPopup(true);
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sol taraf - Arka plan resmi (sadece desktop'ta görünür) */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/images/login-bg.jpg)',
            backgroundSize: 'cover'
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
      </div>
      
      {/* Sağ taraf - Kayıt formu */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 pt-20 sm:pt-24 bg-white">
        <div className="w-full max-w-2xl">
          {/* Logo ve Başlık */}
              <div className="text-center mb-8 lg:mb-10">
                <div className="flex justify-center mb-4 lg:mb-6">
                  <Image
                    src="/images/logos/TRINK-LOGO.svg"
                    alt="Trink Emlak"
                    width={300}
                    height={135}
                    className="h-24 sm:h-30 w-auto"
                    priority
                    unoptimized
                  />
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 lg:mb-3">
                  {t('title')}
                </h1>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Ad ve Soyad */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('firstName')} {t('required')}
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`w-full px-4 py-3 sm:py-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm ${
                        firstNameError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('firstNamePlaceholder')}
                      required
                    />
                    {firstNameError && (
                      <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>{firstNameError}</span>
                      </div>
                    )}
                    {!firstNameError && formData.firstName && validateName(formData.firstName) && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Ad geçerli</span>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('lastName')} {t('required')}
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`w-full px-4 py-3 sm:py-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm ${
                        lastNameError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('lastNamePlaceholder')}
                      required
                    />
                    {lastNameError && (
                      <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>{lastNameError}</span>
                      </div>
                    )}
                    {!lastNameError && formData.lastName && validateName(formData.lastName) && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Soyad geçerli</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* E-posta */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('email')}
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-10 py-3 sm:py-4 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent shadow-sm transition-colors ${
                        emailError 
                          ? 'border-red-500 focus:ring-red-500' 
                          : formData.email && validateEmail(formData.email)
                            ? 'border-green-500 focus:ring-green-500'
                            : 'border-gray-300 focus:ring-red-500'
                      }`}
                      placeholder={t('emailPlaceholder')}
                    />
                    {formData.email && validateEmail(formData.email) && !emailError && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 6L12 13L2 6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  {emailError && (
                    <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>{emailError}</span>
                    </div>
                  )}
                  {!emailError && formData.email && validateEmail(formData.email) && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>E-posta geçerli</span>
                    </div>
                  )}
                </div>

                {/* Telefon */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('phone')} {t('required')}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7294C21.7209 20.9846 21.5573 21.2136 21.3521 21.4019C21.1469 21.5902 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0973 21.9452 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3146 6.72533 15.2661 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.09477 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65171C2.82196 2.44673 3.04971 2.28291 3.30351 2.17052C3.55731 2.05813 3.83166 2.00026 4.10999 2H7.10999C7.59522 1.99522 8.06577 2.16708 8.43376 2.48353C8.80175 2.79999 9.042 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97348 7.27675 9.89381 7.64958C9.81415 8.02242 9.62976 8.36256 9.35999 8.63L8.08999 9.9C9.51355 12.4136 11.5864 14.4865 14.1 15.91L15.37 14.64C15.6374 14.3702 15.9776 14.1858 16.3504 14.1062C16.7233 14.0265 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="absolute left-10 top-1/2 transform -translate-y-1/2 z-10 text-gray-500 font-medium text-base">
                        +90
                      </div>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        autoComplete="tel-national"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full pl-20 pr-4 py-3 sm:py-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm ${
                          phoneError ? 'border-red-500' : isPhoneVerified ? 'border-green-500' : 'border-gray-300'
                        }`}
                        placeholder={t('phonePlaceholderRegister')}
                        maxLength={19}
                        required
                        disabled={isPhoneVerified}
                      />
                      {isPhoneVerified && (
                        <div className="absolute right-3 z-10">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 12L11 14L15 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleVerifyPhone}
                      disabled={isPhoneVerified}
                      className={`px-4 sm:px-6 py-3 sm:py-4 rounded-md font-medium text-sm sm:text-base whitespace-nowrap transition-colors ${
                        isPhoneVerified
                          ? 'bg-green-100 text-green-700 cursor-not-allowed'
                          : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {isPhoneVerified ? t('phoneVerified') : t('verifyPhone')}
                    </button>
                  </div>
                  {phoneError && (
                    <p className="text-red-500 text-sm mt-1">{phoneError}</p>
                  )}
                </div>

                {/* TC Kimlik Numarası */}
                <div>
                  <label htmlFor="tcIdentityNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    TC Kimlik Numarası {t('required')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="tcIdentityNumber"
                      value={formData.tcIdentityNumber}
                      onChange={(e) => handleInputChange('tcIdentityNumber', e.target.value)}
                      className={`w-full px-4 py-3 sm:py-4 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm transition-colors ${
                        tcIdentityError 
                          ? 'border-red-500 focus:ring-red-500' 
                          : formData.tcIdentityNumber.length === 11 && !tcIdentityError
                            ? 'border-green-500 focus:ring-green-500'
                            : 'border-gray-300'
                      }`}
                      placeholder="11 haneli TC Kimlik Numarası"
                      maxLength={11}
                      required
                    />
                    {formData.tcIdentityNumber.length === 11 && !tcIdentityError && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {tcIdentityError && (
                    <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>{tcIdentityError}</span>
                    </div>
                  )}
                  {!tcIdentityError && formData.tcIdentityNumber.length > 0 && formData.tcIdentityNumber.length < 11 && (
                    <div className="mt-1 text-xs text-gray-500">
                      {11 - formData.tcIdentityNumber.length} karakter daha girin
                    </div>
                  )}
                </div>

                {/* Şifre */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('password')} {t('required')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`w-full px-10 py-3 sm:py-4 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent shadow-sm transition-colors ${
                        passwordError 
                          ? 'border-red-500 focus:ring-red-500' 
                          : isPasswordValid
                            ? 'border-green-500 focus:ring-green-500'
                            : 'border-gray-300 focus:ring-red-500'
                      }`}
                      placeholder={t('passwordPlaceholder')}
                      required
                    />
                    {isPasswordValid && (
                      <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2"/>
                        <circle cx="12" cy="16" r="1" fill="#9CA3AF"/>
                        <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="#9CA3AF" strokeWidth="2"/>
                      </svg>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 12C2 12 5.63636 6 12 6C18.3636 6 22 12 22 12C22 12 18.3636 18 12 18C5.63636 18 2 12 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 2L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  
                  {passwordError && (
                    <div className="mt-2 flex items-center gap-1 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>{passwordError}</span>
                    </div>
                  )}
                  
                  {/* Şifre Gereksinimleri */}
                  {formData.password && (
                    <div className="mt-3 bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                      <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {t('passwordRequirements.title')}
                      </p>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                            passwordRequirements.minLength ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            {passwordRequirements.minLength && (
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <span className={`text-xs sm:text-sm ${
                            passwordRequirements.minLength ? 'text-green-700 font-medium' : 'text-gray-600'
                          }`}>
                            {t('passwordRequirements.minLength')}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                            passwordRequirements.hasNumber ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            {passwordRequirements.hasNumber && (
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <span className={`text-xs sm:text-sm ${
                            passwordRequirements.hasNumber ? 'text-green-700 font-medium' : 'text-gray-600'
                          }`}>
                            {t('passwordRequirements.hasNumber')}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                            passwordRequirements.hasLowercase ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            {passwordRequirements.hasLowercase && (
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <span className={`text-xs sm:text-sm ${
                            passwordRequirements.hasLowercase ? 'text-green-700 font-medium' : 'text-gray-600'
                          }`}>
                            {t('passwordRequirements.hasLowercase')}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                            passwordRequirements.hasUppercase ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            {passwordRequirements.hasUppercase && (
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <span className={`text-xs sm:text-sm ${
                            passwordRequirements.hasUppercase ? 'text-green-700 font-medium' : 'text-gray-600'
                          }`}>
                            {t('passwordRequirements.hasUppercase')}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                            passwordRequirements.hasSpecialChar ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            {passwordRequirements.hasSpecialChar && (
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <span className={`text-xs sm:text-sm ${
                            passwordRequirements.hasSpecialChar ? 'text-green-700 font-medium' : 'text-gray-600'
                          }`}>
                            {t('passwordRequirements.hasSpecialChar')}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Şifre Tekrar */}
                <div>
                  <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('passwordConfirm')} {t('required')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswordConfirm ? "text" : "password"}
                      id="passwordConfirm"
                      value={formData.passwordConfirm}
                      onChange={(e) => handleInputChange('passwordConfirm', e.target.value)}
                      className="w-full px-10 py-3 sm:py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm"
                      placeholder={t('passwordConfirmPlaceholder')}
                      required
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2"/>
                        <circle cx="12" cy="16" r="1" fill="#9CA3AF"/>
                        <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="#9CA3AF" strokeWidth="2"/>
                      </svg>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {showPasswordConfirm ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 12C2 12 5.63636 6 12 6C18.3636 6 22 12 22 12C22 12 18.3636 18 12 18C5.63636 18 2 12 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 2L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Sözleşme Onayı */}
                <div className="flex items-start space-x-3 pt-2">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    required
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-gray-600 leading-relaxed">
                    <button
                      type="button"
                      onClick={() => handleContractClick('UYELIKVEKULLANIM')}
                      className="text-red-600 font-medium hover:underline"
                    >
                      {footerT('termsOfUse')}
                    </button>
                    {t('termsText')}
                    <button
                      type="button"
                      onClick={() => handleContractClick('KVKKAYDINLATMAMETNI')}
                      className="text-red-600 font-medium hover:underline"
                    >
                      {footerT('privacyPolicy')}
                    </button>
                    {t('termsText2')} {t('required')}
                  </label>
                </div>

                {/* Error message */}
                {(error || errors.length > 0) && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    {error && (
                      <p className="text-red-600 text-sm mb-2">{error}</p>
                    )}
                    {errors.length > 0 && (
                      <div>
                        {errors.map((err: string, index: number) => (
                          <p key={index} className="text-red-600 text-sm">{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Kayıt Ol Butonu */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 text-white py-3 sm:py-4 px-4 rounded-lg font-bold text-base sm:text-lg hover:bg-red-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('creatingAccount') || 'Hesap oluşturuluyor...' : t('createAccount')}
                </button>

                {/* Giriş Yap Linki */}
                <div className="text-center">
                  <span className="text-sm text-gray-600">
                    {t('alreadyHaveAccount')}
                  </span>
                  <Link
                    href={`/${locale}/giris`}
                    className="text-sm text-red-600 font-medium hover:underline ml-1"
                  >
                    {t('loginLink')}
                  </Link>
                </div>
              </form>
          </div>
        </div>

      {/* Phone Verification Popup */}
      <PhoneVerificationPopup
        isOpen={showPhoneVerification}
        onClose={() => setShowPhoneVerification(false)}
        phoneNumber={formData.phone}
        onVerificationSuccess={handlePhoneVerificationSuccess}
        codeType={SmsCodeType.Register}
      />

      {/* Contract Popup */}
      <ContractPopup
        isOpen={showContractPopup}
        onClose={() => setShowContractPopup(false)}
        contractType={selectedContract}
        locale={locale}
      />
    </div>
  );
}